// Alta manual de cliente FUERA DE ZONA — excepción controlada del admin.
// El cliente vive en barrio sin cobertura; un socio (Gimena, Nico, Roberto u otro)
// se hace cargo personalmente de la entrega.

import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import {
  buscarPersonas, buscarPorDocumento, crearPersona,
  crearSuscripcionFueraZona, getKits,
} from '../../lib/clientesApi'
import { validarTelUY, formatearTelUY, validarEmail, validarDocumento } from '../../lib/documentos'
import { supabase } from '../../lib/supabase'
import { VENTANAS_HORARIAS } from '../../lib/ventanasHorarias'

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL

const TIPO_ACCESO = [
  { v: 'solo_registro', label: '🔒 Solo registro interno', desc: 'No tiene login. Cobro 100% manual. Comunicación vía Gimena.' },
  { v: 'portal',        label: '🌐 Con acceso al portal',  desc: 'Ve sus pedidos, próxima entrega, paga online (MP). Email obligatorio.' },
]

const RESPONSABLES = [
  { v: 'gimena',  label: '👩 Gimena',  desc: 'Vive en Prado · entrega a sus contactos' },
  { v: 'nico',    label: '👨 Nico',    desc: 'Entrega personal' },
  { v: 'roberto', label: '👨 Roberto', desc: 'Entrega personal' },
  { v: 'otro',    label: '✋ Otro',    desc: 'Detallar en notas' },
]

const TIPO_VIVIENDA = [
  { v: 'casa',     label: '🏠 Casa' },
  { v: 'edificio', label: '🏢 Edificio' },
  { v: 'oficina', label: '🏢 Oficina' },
]

// Reutilizamos las VENTANAS_HORARIAS de lib/ para mantener keys sincronizadas con el enum BD.
const VENTANA = VENTANAS_HORARIAS.map(v => ({ v: v.key, label: `${v.emoji} ${v.label}` }))

const MODELO_COBRO = [
  { v: 'prepaid_mensiversario', label: 'Prepago mensiversario (cobra día anterior a entrega)' },
  { v: 'manual',                label: 'Cobro manual (no automático)' },
]

export default function ClienteFueraZona() {
  const navigate = useNavigate()

  // Persona (titular)
  const [busqueda, setBusqueda] = useState('')
  const [resultados, setResultados] = useState([])
  const [titular, setTitular] = useState(null)
  const [showCrearPersona, setShowCrearPersona] = useState(false)

  // Tipo de acceso
  const [tipoAcceso, setTipoAcceso] = useState('solo_registro')

  // Datos suscripción
  const [alias, setAlias] = useState('')
  const [barrioNombre, setBarrioNombre] = useState('')
  const [direccion, setDireccion] = useState('')
  const [apto, setApto] = useState('')
  const [tipoVivienda, setTipoVivienda] = useState('')
  const [diaEntrega, setDiaEntrega] = useState('')
  const [ventanaHoraria, setVentanaHoraria] = useState('manana')
  const [diaCobroMes, setDiaCobroMes] = useState('')
  const [modeloCobro, setModeloCobro] = useState('prepaid_mensiversario')
  const [entregaResponsable, setEntregaResponsable] = useState('gimena')
  const [kitCodigo, setKitCodigo] = useState('')
  const [kits, setKits] = useState([])
  const [notas, setNotas] = useState('')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    getKits().then(setKits).catch(() => {})
  }, [])

  // Búsqueda de personas
  useEffect(() => {
    if (busqueda.trim().length < 2) { setResultados([]); return }
    const t = setTimeout(async () => {
      const r = await buscarPersonas(busqueda, 8)
      setResultados(r)
    }, 250)
    return () => clearTimeout(t)
  }, [busqueda])

  async function handleSubmit() {
    setError(null); setSuccess(null)
    if (!titular) { setError('Buscá o creá una persona como titular antes de guardar.'); return }
    if (!direccion.trim()) { setError('Falta dirección (calle + número).'); return }
    if (!entregaResponsable) { setError('Indicá quién se hace cargo de la entrega.'); return }
    if (!barrioNombre.trim()) { setError('Indicá el barrio (texto libre).'); return }
    if (tipoAcceso === 'portal' && !titular.email) {
      setError('Para acceso al portal el titular necesita tener email cargado. Editá la persona y agregalo.')
      return
    }

    setSaving(true)
    try {
      const cli = await crearSuscripcionFueraZona({
        titularId: titular.id,
        alias: alias.trim() || null,
        barrioNombre: barrioNombre.trim(),
        direccion: direccion.trim(),
        apto: apto.trim() || null,
        tipoVivienda: tipoVivienda || null,
        kitCodigo: kitCodigo || null,
        diaEntrega: diaEntrega || null,
        ventanaHoraria,
        diaCobroMes: diaCobroMes ? Number(diaCobroMes) : null,
        modeloCobro,
        entregaResponsable,
        notas: notas.trim() || null,
      })

      // Si eligió portal, mandar invitación
      if (tipoAcceso === 'portal') {
        try {
          const { data: { session } } = await supabase.auth.getSession()
          const r = await fetch(`${SUPA_URL}/functions/v1/invitar-cliente-fuera-zona`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              profile_id: titular.id,
              email: titular.email,
              cliente_id: cli.id,
            }),
          })
          const j = await r.json()
          if (!r.ok || !j.success) throw new Error(j.error || 'Falló la invitación')
          setSuccess(`✅ Suscripción creada. ${j.mensaje}`)
        } catch (e) {
          setSuccess(`Suscripción creada (ID: ${cli.id}) — ⚠️ pero la invitación al portal falló: ${e.message}. Podés reintentar desde /admin/clientes.`)
        }
      } else {
        setSuccess(`✅ Suscripción creada (solo registro interno). ID: ${cli.id}`)
      }

      setTimeout(() => navigate('/admin/clientes'), 2500)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminLayout title="🚫 Alta manual — Cliente fuera de zona">
      <div style={{ maxWidth: 720, margin: '0 auto' }}>

        <Link to="/admin/clientes" style={{ fontSize: 13, color: '#64748B', textDecoration: 'none' }}>
          ← Volver a Clientes
        </Link>

        <div style={{
          background: '#FFFBEB', border: '1px solid #FDE68A',
          borderRadius: 10, padding: 14, marginTop: 12, marginBottom: 20,
          fontSize: 13, lineHeight: 1.5, color: '#78350F',
        }}>
          <strong>⚠️ Excepción controlada.</strong> Estos clientes <strong>NO</strong> entran al ruteo
          automático ni a etiquetas de reparto estándar. Alguien (un socio) se hace cargo personalmente
          de la entrega. Usar sólo cuando la persona vive fuera de cobertura y hay un responsable confirmado.
        </div>

        {/* 1. Titular */}
        <Section n="1" titulo="¿Quién es el titular?">
          {titular ? (
            <div style={pillTitular}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{titular.nombre}</div>
                <div style={{ fontSize: 12, color: '#64748B' }}>
                  {titular.phone || '—'} {titular.email ? `· ${titular.email}` : ''}
                </div>
              </div>
              <button className="btn ghost" onClick={() => setTitular(null)}>Cambiar</button>
            </div>
          ) : (
            <>
              <input
                type="search"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, teléfono, email o documento…"
                style={{ width: '100%', padding: 10, fontSize: 14, borderRadius: 8, border: '1px solid #CBD5E1', boxSizing: 'border-box' }}
              />
              {resultados.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                  {resultados.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => { setTitular(p); setBusqueda(''); setResultados([]) }}
                      style={{ display: 'block', width: '100%', padding: 10, textAlign: 'left', background: 'none', border: 0, borderBottom: '1px solid #F1F5F9', cursor: 'pointer' }}>
                      <strong>{p.nombre}</strong> <span style={{ color: '#64748B', fontSize: 12 }}>· {p.phone || '—'} {p.email ? `· ${p.email}` : ''}</span>
                    </button>
                  ))}
                </div>
              )}
              {!showCrearPersona && (
                <button className="btn ghost" onClick={() => setShowCrearPersona(true)} style={{ marginTop: 8 }}>
                  + Crear persona nueva
                </button>
              )}
              {showCrearPersona && (
                <CrearPersonaForm
                  onCreada={p => { setTitular(p); setShowCrearPersona(false); setBusqueda('') }}
                  onCancel={() => setShowCrearPersona(false)}
                />
              )}
            </>
          )}
        </Section>

        {/* 2. Tipo de acceso */}
        <Section n="2" titulo="¿Cómo manejamos a este cliente?">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {TIPO_ACCESO.map(t => (
              <button
                key={t.v} type="button"
                onClick={() => setTipoAcceso(t.v)}
                style={{
                  textAlign: 'left', padding: 14, borderRadius: 10,
                  border: tipoAcceso === t.v ? '2px solid #00A8B5' : '1px solid #E2E8F0',
                  background: tipoAcceso === t.v ? '#F0FDFA' : 'white',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>{t.label}</div>
                <div style={{ fontSize: 11, color: '#64748B', marginTop: 4, lineHeight: 1.4 }}>{t.desc}</div>
              </button>
            ))}
          </div>
          {tipoAcceso === 'portal' && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#0F172A', background: '#FEF3C7', border: '1px solid #FDE68A', padding: 10, borderRadius: 8 }}>
              ⚠️ El titular debe tener <strong>email cargado</strong>. Al guardar se le manda magic link para entrar al portal.
              Como <code>fuera_zona = true</code>, en el portal va a ver banner "tu zona la cubre Gimena" y no se mete al ruteo automático.
            </div>
          )}
        </Section>

        {/* 3. Quién entrega */}
        <Section n="3" titulo="¿Quién se hace cargo de la entrega?">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {RESPONSABLES.map(r => (
              <button
                key={r.v} type="button"
                onClick={() => setEntregaResponsable(r.v)}
                style={{
                  textAlign: 'left', padding: 12, borderRadius: 10,
                  border: entregaResponsable === r.v ? '2px solid #00A8B5' : '1px solid #E2E8F0',
                  background: entregaResponsable === r.v ? '#F0FDFA' : 'white',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>{r.label}</div>
                <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{r.desc}</div>
              </button>
            ))}
          </div>
        </Section>

        {/* 4. Dirección de entrega */}
        <Section n="4" titulo="Dirección de entrega (libre, no del catálogo de barrios)">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Barrio (texto libre)" required>
              <input type="text" value={barrioNombre} onChange={e => setBarrioNombre(e.target.value)}
                placeholder="Ej: Prado, La Blanqueada, Cerro…" style={inp} />
            </Field>
            <Field label="Tipo de vivienda">
              <select value={tipoVivienda} onChange={e => setTipoVivienda(e.target.value)} style={inp}>
                <option value="">—</option>
                {TIPO_VIVIENDA.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Calle + número" required>
            <input type="text" value={direccion} onChange={e => setDireccion(e.target.value)}
              placeholder="Ej: Av. Buschental 3045" style={inp} />
          </Field>
          <Field label="Apto / detalle (opcional)">
            <input type="text" value={apto} onChange={e => setApto(e.target.value)}
              placeholder="Ej: Apto 502" style={inp} />
          </Field>
          <Field label="Alias del cliente (opcional)">
            <input type="text" value={alias} onChange={e => setAlias(e.target.value)}
              placeholder="Ej: Casa Mamá Gime" style={inp} />
          </Field>
        </Section>

        {/* 5. Kit + agenda */}
        <Section n="5" titulo="Kit y agenda de entrega">
          <Field label="Kit">
            <select value={kitCodigo} onChange={e => setKitCodigo(e.target.value)} style={inp}>
              <option value="">— sin kit asignado —</option>
              {kits.map(k => <option key={k.codigo} value={k.codigo}>{k.tier} · {k.nombre}</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Día de entrega (texto libre)">
              <input type="text" value={diaEntrega} onChange={e => setDiaEntrega(e.target.value)}
                placeholder="Ej: lunes / 1° del mes" style={inp} />
            </Field>
            <Field label="Ventana horaria">
              <select value={ventanaHoraria} onChange={e => setVentanaHoraria(e.target.value)} style={inp}>
                {VENTANA.map(v => <option key={v.v} value={v.v}>{v.label}</option>)}
              </select>
            </Field>
          </div>
        </Section>

        {/* 6. Cobro */}
        <Section n="6" titulo="Cobro">
          <Field label="Modelo">
            <select value={modeloCobro} onChange={e => setModeloCobro(e.target.value)} style={inp}>
              {MODELO_COBRO.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
          </Field>
          <Field label="Día del mes para cobrar (opcional, 1-28)">
            <input type="number" min="1" max="28" value={diaCobroMes}
              onChange={e => setDiaCobroMes(e.target.value)} placeholder="Ej: 5" style={inp} />
          </Field>
        </Section>

        {/* 7. Notas */}
        <Section n="7" titulo="Notas internas (opcional)">
          <Field label="Cualquier detalle (timing, contacto secundario, condición especial)">
            <textarea value={notas} onChange={e => setNotas(e.target.value)} rows="3" style={{ ...inp, resize: 'vertical' }} />
          </Field>
        </Section>

        {/* Acciones */}
        {error && (
          <div style={{ background: '#FEF2F2', color: '#991B1B', padding: 12, borderRadius: 8, marginTop: 16, fontSize: 13 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#F0FDF4', color: '#166534', padding: 12, borderRadius: 8, marginTop: 16, fontSize: 13 }}>
            ✅ {success}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24, paddingBottom: 40 }}>
          <Link to="/admin/clientes" className="btn ghost">Cancelar</Link>
          <button className="btn primary" onClick={handleSubmit} disabled={saving || !titular}>
            {saving ? 'Guardando…' : '✓ Crear cliente fuera de zona'}
          </button>
        </div>
      </div>
    </AdminLayout>
  )
}

/* ─── Sub-componentes ─── */

function Section({ n, titulo, children }) {
  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, marginTop: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ background: '#1A3461', color: 'white', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
          {n}
        </span>
        {titulo}
      </h3>
      {children}
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginTop: 8 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
        {label} {required && <span style={{ color: '#DC2626' }}>*</span>}
      </label>
      {children}
    </div>
  )
}

const inp = {
  width: '100%', padding: 10, fontSize: 14, borderRadius: 8,
  border: '1px solid #CBD5E1', fontFamily: 'inherit', boxSizing: 'border-box',
}

const pillTitular = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: 10,
  padding: 12,
}

function CrearPersonaForm({ onCreada, onCancel }) {
  const [nombre, setNombre] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [docTipo, setDocTipo] = useState('ci')
  const [docNumero, setDocNumero] = useState('')
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  async function handleCrear() {
    setError(null)
    if (!nombre.trim()) { setError('Falta nombre'); return }
    if (phone && !validarTelUY(phone)) { setError('Teléfono inválido (formato UY)'); return }
    if (email && !validarEmail(email)) { setError('Email inválido'); return }

    // Validamos y normalizamos documento (igual que Clientes.jsx)
    let docNormalizado = null
    if (docNumero) {
      const v = validarDocumento(docTipo, docNumero)
      if (!v.ok) { setError(v.msg || 'Documento inválido'); return }
      docNormalizado = v.normalizado
    }

    setSaving(true)
    try {
      // Detectar duplicado por documento usando el valor NORMALIZADO
      // (sin esto, "4.123.456-7" no matchea "41234567" y duplica el registro)
      if (docNormalizado && docTipo) {
        const dup = await buscarPorDocumento(docTipo, docNormalizado)
        if (dup) {
          if (confirm(`Ya existe alguien con ese documento: ${dup.nombre}. ¿Usar esa persona?`)) {
            onCreada(dup); return
          } else {
            setSaving(false); return
          }
        }
      }
      const p = await crearPersona({
        nombre, phone: phone ? formatearTelUY(phone) : null,
        email: email || null,
        documento: docNormalizado, documentoTipo: docNormalizado ? docTipo : null,
      })
      if (p) onCreada(p)
    } catch (e) { setError(e.message || String(e)) }
    finally { setSaving(false) }
  }

  return (
    <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: 12, marginTop: 8 }}>
      <Field label="Nombre completo" required>
        <input type="text" value={nombre} onChange={e => setNombre(e.target.value)} style={inp} />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Teléfono (UY)">
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="098 610 001" style={inp} />
        </Field>
        <Field label="Email">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inp} />
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10 }}>
        <Field label="Doc. tipo">
          <select value={docTipo} onChange={e => setDocTipo(e.target.value)} style={inp}>
            <option value="ci">CI UY</option>
            <option value="rut">RUT</option>
            <option value="pasaporte">Pasaporte</option>
          </select>
        </Field>
        <Field label="Documento (opcional)">
          <input type="text" value={docNumero} onChange={e => setDocNumero(e.target.value)} style={inp} />
        </Field>
      </div>
      {error && <div style={{ color: '#DC2626', fontSize: 12, marginTop: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 10 }}>
        <button className="btn ghost" onClick={onCancel} disabled={saving}>Cancelar</button>
        <button className="btn primary" onClick={handleCrear} disabled={saving}>
          {saving ? '...' : 'Crear persona'}
        </button>
      </div>
    </div>
  )
}
